import { useEffect, useId, useRef } from "react";

export interface CandidateFormState {
  firstName: string;
  lastName: string;
  email: string;
  currentRoleTitle: string;
  currentCompany: string;
  location: string;
}

export const INITIAL_CANDIDATE_FORM: CandidateFormState = {
  firstName: "",
  lastName: "",
  email: "",
  currentRoleTitle: "",
  currentCompany: "",
  location: "",
};

interface CandidateFormFieldsProps {
  form: CandidateFormState;
  onChange: (form: CandidateFormState) => void;
  /** Move focus to the first-name input when this flips to true. */
  autoFocusFirstField?: boolean;
}

const INPUT_CLASS =
  "mt-1 block w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";
const LABEL_CLASS = "block text-xs font-medium text-text-secondary";

export function CandidateFormFields({
  form,
  onChange,
  autoFocusFirstField = false,
}: CandidateFormFieldsProps) {
  const id = useId();
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocusFirstField) firstNameRef.current?.focus();
  }, [autoFocusFirstField]);

  const set = (key: keyof CandidateFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...form, [key]: e.target.value });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label htmlFor={`${id}-first-name`} className={LABEL_CLASS}>
          First name *
        </label>
        <input
          id={`${id}-first-name`}
          ref={firstNameRef}
          type="text"
          required
          value={form.firstName}
          onChange={set("firstName")}
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor={`${id}-last-name`} className={LABEL_CLASS}>
          Last name *
        </label>
        <input
          id={`${id}-last-name`}
          type="text"
          required
          value={form.lastName}
          onChange={set("lastName")}
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor={`${id}-email`} className={LABEL_CLASS}>
          Email
        </label>
        <input
          id={`${id}-email`}
          type="email"
          value={form.email}
          onChange={set("email")}
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor={`${id}-role`} className={LABEL_CLASS}>
          Current role
        </label>
        <input
          id={`${id}-role`}
          type="text"
          value={form.currentRoleTitle}
          onChange={set("currentRoleTitle")}
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor={`${id}-company`} className={LABEL_CLASS}>
          Current company
        </label>
        <input
          id={`${id}-company`}
          type="text"
          value={form.currentCompany}
          onChange={set("currentCompany")}
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <label htmlFor={`${id}-location`} className={LABEL_CLASS}>
          Location
        </label>
        <input
          id={`${id}-location`}
          type="text"
          value={form.location}
          onChange={set("location")}
          className={INPUT_CLASS}
        />
      </div>
    </div>
  );
}
